import invariant from "tiny-invariant";
import {
    DeleteAllInstancesRequest,
    DeleteOneInstanceRequest,
    OverrideDateRequest,
    OverrideDescriptionRequest,
    OverrideDurationRequest,
    OverrideProjectRequest,
    OverrideStatusRequest,
    OverrideTagRequest,
    OverrideTimeRequest,
    OverrideTimeSpentMinsRequest,
    OverrideTitleRequest,
    RemoveDurationRequest,
    RemoveTimeRequest,
} from "@lifeat/shared/task-management/RecurringTaskFunctionContracts";
import { RecurringMasterTask, RecurringOverrideTask } from "@lifeat/shared/task-management/RecurringTaskTypes";
import { buildBlankOverride } from "@lifeat/shared/task-management/RecurringTaskUtil";
import {
    withDuration,
    withScheduleDate,
    withScheduleTime,
    withoutDuration,
    withoutScheduleTime,
} from "@lifeat/shared/task-management/TaskManagementUtil";
import { withMutex } from "@lifeat/shared/util/AsyncUtil";
import { useToastListContext } from "@contexts/ToastListContext";
import { callMonolith } from "@service/ServerlessFunctionsService";
import { useTaskListContext } from "./TaskListContext";
import { taskManagementOperationMutex } from "./TaskManagementGlobals";

export const useRecurringTaskMethods = () => {
    const { recurringMasters$, recurringOverrides$ } = useTaskListContext();
    const { addToastNotification } = useToastListContext();

    const overrideTitle = withMutex(taskManagementOperationMutex, async (request: OverrideTitleRequest) => {
        try {
            overrideFieldLocally(request.masterId, request.index, "title", request.title);
            await callMonolith("override-recurring-task-title", request);
        } catch (err: unknown) {
            addToastNotification("Failed to override title", String(err));
        }
    });

    const overrideDescription = withMutex(taskManagementOperationMutex, async (request: OverrideDescriptionRequest) => {
        try {
            overrideFieldLocally(request.masterId, request.index, "description", request.description);
            await callMonolith("override-recurring-task-description", request);
        } catch (err: unknown) {
            addToastNotification("Failed to override description", String(err));
        }
    });

    const overrideStatus = withMutex(taskManagementOperationMutex, async (request: OverrideStatusRequest) => {
        try {
            overrideFieldLocally(request.masterId, request.index, "status", request.status);
            await callMonolith("override-recurring-task-status", request);
        } catch (err: unknown) {
            addToastNotification("Failed to override status", String(err));
        }
    });

    const overrideTimeSpentMins = withMutex(
        taskManagementOperationMutex,
        async (request: OverrideTimeSpentMinsRequest) => {
            try {
                overrideFieldLocally(request.masterId, request.index, "timeSpentMins", request.timeSpentMins);
                await callMonolith("override-recurring-task-time-spent-mins", request);
            } catch (err: unknown) {
                addToastNotification("Failed to override time spent", String(err));
            }
        },
    );

    const overrideTag = withMutex(taskManagementOperationMutex, async (request: OverrideTagRequest) => {
        try {
            overrideFieldLocally(request.masterId, request.index, "tagId", request.tagId);
            await callMonolith("override-recurring-task-tag", request);
        } catch (err: unknown) {
            addToastNotification("Failed to override tag", String(err));
        }
    });

    const overrideProject = withMutex(taskManagementOperationMutex, async (request: OverrideProjectRequest) => {
        try {
            overrideFieldLocally(request.masterId, request.index, "projectId", request.projectId);
            await callMonolith("override-recurring-task-project", request);
        } catch (err: unknown) {
            addToastNotification("Failed to override project", String(err));
        }
    });

    const deleteOneInstance = withMutex(taskManagementOperationMutex, async (request: DeleteOneInstanceRequest) => {
        try {
            const overridenTask = {
                ...recurringOverrides$.value[request.masterId]?.[request.index],
                isDeleted: true,
            };
            const currentOverrides = { ...recurringOverrides$.value };
            const currentOverridesOfMasterId = { ...currentOverrides[request.masterId] };
            currentOverridesOfMasterId[request.index] = overridenTask;
            currentOverrides[request.masterId] = currentOverridesOfMasterId;
            recurringOverrides$.value = currentOverrides;
            await callMonolith("delete-one-recurring-task", request);
        } catch (err: unknown) {
            addToastNotification("Failed to delete one date from recurring task", String(err));
        }
    });

    const deleteAllInstances = withMutex(taskManagementOperationMutex, async (request: DeleteAllInstancesRequest) => {
        try {
            const overridesWithoutId: Record<string, Record<number, RecurringOverrideTask>> = {};
            for (const [masterId, override] of Object.entries(recurringOverrides$.value)) {
                if (masterId !== request.masterId) {
                    overridesWithoutId[masterId] = override;
                }
            }
            const mastersWithoutId: Record<string, RecurringMasterTask> = {};
            for (const [masterId, master] of Object.entries(recurringMasters$.value)) {
                if (masterId !== request.masterId) {
                    mastersWithoutId[masterId] = master;
                }
            }

            recurringOverrides$.value = overridesWithoutId;
            recurringMasters$.value = mastersWithoutId;
            await callMonolith("delete-all-recurring-tasks", request);
        } catch (err: unknown) {
            addToastNotification("Failed to delete all dates from recurring task", String(err));
        }
    });

    const overrideDuration = withMutex(taskManagementOperationMutex, async (request: OverrideDurationRequest) => {
        try {
            const master = recurringMasters$.value[request.masterId];
            invariant(master, `Recurring master ${request.masterId} not found`);
            const override =
                recurringOverrides$.value[request.masterId]?.[request.index] ??
                buildBlankOverride({ id: request.masterId, document: master }, request.index);
            const next = {
                ...override,
                schedulingDetails: withDuration(override.schedulingDetails, request.durationMins),
            };
            const currentOverrides = { ...recurringOverrides$.value };
            const currentOverridesOfMasterId = { ...currentOverrides[request.masterId] };
            currentOverridesOfMasterId[request.index] = next;
            currentOverrides[request.masterId] = currentOverridesOfMasterId;
            recurringOverrides$.value = currentOverrides;
            await callMonolith("override-recurring-task-duration", request);
        } catch (err: unknown) {
            addToastNotification("Failed to override duration", String(err));
        }
    });

    const overrideDate = withMutex(taskManagementOperationMutex, async (request: OverrideDateRequest) => {
        try {
            const master = recurringMasters$.value[request.masterId];
            invariant(master, `Recurring master ${request.masterId} not found`);
            const override =
                recurringOverrides$.value[request.masterId]?.[request.index] ??
                buildBlankOverride({ id: request.masterId, document: master }, request.index);
            const next = {
                ...override,
                schedulingDetails: withScheduleDate(override.schedulingDetails, request.date),
            };
            const currentOverrides = { ...recurringOverrides$.value };
            const currentOverridesOfMasterId = { ...currentOverrides[request.masterId] };
            currentOverridesOfMasterId[request.index] = next;
            currentOverrides[request.masterId] = currentOverridesOfMasterId;
            recurringOverrides$.value = currentOverrides;
            await callMonolith("override-recurring-task-date", request);
        } catch (err: unknown) {
            addToastNotification("Failed to override date", String(err));
        }
    });

    const overrideTime = withMutex(taskManagementOperationMutex, async (request: OverrideTimeRequest) => {
        try {
            const master = recurringMasters$.value[request.masterId];
            invariant(master, `Recurring master ${request.masterId} not found`);
            const override =
                recurringOverrides$.value[request.masterId]?.[request.index] ??
                buildBlankOverride({ id: request.masterId, document: master }, request.index);
            const next = {
                ...override,
                schedulingDetails: withScheduleTime(override.schedulingDetails, request.time),
            };
            const currentOverrides = { ...recurringOverrides$.value };
            const currentOverridesOfMasterId = { ...currentOverrides[request.masterId] };
            currentOverridesOfMasterId[request.index] = next;
            currentOverrides[request.masterId] = currentOverridesOfMasterId;
            recurringOverrides$.value = currentOverrides;
            await callMonolith("override-recurring-task-time", request);
        } catch (err: unknown) {
            addToastNotification("Failed to override time", String(err));
        }
    });

    const removeDuration = withMutex(taskManagementOperationMutex, async (request: RemoveDurationRequest) => {
        try {
            const master = recurringMasters$.value[request.masterId];
            invariant(master, `Recurring master ${request.masterId} not found`);
            const override =
                recurringOverrides$.value[request.masterId]?.[request.index] ??
                buildBlankOverride({ id: request.masterId, document: master }, request.index);
            const next = {
                ...override,
                schedulingDetails: withoutDuration(override.schedulingDetails) ?? override.schedulingDetails,
            };
            const currentOverrides = { ...recurringOverrides$.value };
            const currentOverridesOfMasterId = { ...currentOverrides[request.masterId] };
            currentOverridesOfMasterId[request.index] = next;
            currentOverrides[request.masterId] = currentOverridesOfMasterId;
            recurringOverrides$.value = currentOverrides;
            await callMonolith("remove-recurring-task-duration", request);
        } catch (err: unknown) {
            addToastNotification("Failed to remove duration", String(err));
        }
    });

    const removeTime = withMutex(taskManagementOperationMutex, async (request: RemoveTimeRequest) => {
        try {
            const master = recurringMasters$.value[request.masterId];
            invariant(master, `Recurring master ${request.masterId} not found`);
            const override =
                recurringOverrides$.value[request.masterId]?.[request.index] ??
                buildBlankOverride({ id: request.masterId, document: master }, request.index);
            const next = {
                ...override,
                schedulingDetails:
                    withoutScheduleTime(override.schedulingDetails, request.clientTimezone) ??
                    override.schedulingDetails,
            };
            const currentOverrides = { ...recurringOverrides$.value };
            const currentOverridesOfMasterId = { ...currentOverrides[request.masterId] };
            currentOverridesOfMasterId[request.index] = next;
            currentOverrides[request.masterId] = currentOverridesOfMasterId;
            recurringOverrides$.value = currentOverrides;
            await callMonolith("remove-recurring-task-time", request);
        } catch (err: unknown) {
            addToastNotification("Failed to remove time", String(err));
        }
    });

    const removeDueDate = withMutex(taskManagementOperationMutex, async (masterId: string, index: number) => {
        // TODO [LAS-1926]: implement
    });

    const overrideDueDate = withMutex(
        taskManagementOperationMutex,
        async (masterId: string, index: number, dueDate: string) => {
            // TODO [LAS-1926]: implement
        },
    );

    const overrideFieldLocally = <TKey extends keyof RecurringOverrideTask>(
        masterId: string,
        index: number,
        key: TKey,
        value: RecurringOverrideTask[TKey],
    ) => {
        const master = recurringMasters$.value[masterId];
        invariant(master, `Recurring master ${masterId} not found`);
        const override = recurringOverrides$.value[masterId]?.[index];
        const overridenTask = {
            ...(override ?? buildBlankOverride({ id: masterId, document: master }, index)),
            [key]: value,
        };
        const currentOverrides = { ...recurringOverrides$.value };
        const currentOverridesOfMasterId = { ...currentOverrides[masterId] };
        currentOverridesOfMasterId[index] = overridenTask;
        currentOverrides[masterId] = currentOverridesOfMasterId;
        recurringOverrides$.value = currentOverrides;
    };

    return {
        overrideTitle,
        overrideDescription,
        overrideStatus,
        overrideTimeSpentMins,
        overrideTag,
        overrideProject,
        deleteOneInstance,
        deleteAllInstances,
        overrideDuration,
        overrideDate,
        overrideTime,
        removeDuration,
        removeTime,
        removeDueDate,
        overrideDueDate,
    };
};
